import { useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { setAgentFilterSearchParam } from "@/pages/agent/utils/agent-search-params.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import {
  AGENT_ROUTINE_SEARCH_QUERY_SEARCH_PARAM,
  AGENT_ROUTINE_SORT_SEARCH_PARAM,
  AGENT_ROUTINE_STATUS_SEARCH_PARAM,
} from "@/store/agent-overlay/types";

export const ALL_ROUTINE_STATUSES_FILTER = "all-routine-statuses";

export const RoutineSortKey = {
  NextRun: "next-run",
  RecentlyUpdated: "recently-updated",
  Name: "name",
} as const;

export type RoutineSortKey = (typeof RoutineSortKey)[keyof typeof RoutineSortKey];

export const DEFAULT_ROUTINE_SORT: RoutineSortKey = RoutineSortKey.NextRun;

const isRoutineSortKey = (value: string): value is RoutineSortKey =>
  value === RoutineSortKey.NextRun ||
  value === RoutineSortKey.RecentlyUpdated ||
  value === RoutineSortKey.Name;

export const useAgentRoutinesSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();

  const statusFilter = normalizeStatusValue({
    status: searchParams.get(AGENT_ROUTINE_STATUS_SEARCH_PARAM) ?? ALL_ROUTINE_STATUSES_FILTER,
  });
  const searchQuery = searchParams.get(AGENT_ROUTINE_SEARCH_QUERY_SEARCH_PARAM) ?? "";
  const rawSortFilter = searchParams.get(AGENT_ROUTINE_SORT_SEARCH_PARAM) ?? DEFAULT_ROUTINE_SORT;
  const sortFilter: RoutineSortKey = isRoutineSortKey(rawSortFilter)
    ? rawSortFilter
    : DEFAULT_ROUTINE_SORT;

  const updateRoutineFilters = useCallback(
    ({
      nextStatusFilter,
      nextSearchQuery,
      nextSortFilter,
    }: {
      nextStatusFilter?: string;
      nextSearchQuery?: string;
      nextSortFilter?: string;
    }) => {
      setSearchParams(
        previousSearchParams => {
          const nextSearchParams = new URLSearchParams(previousSearchParams);

          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_ROUTINE_STATUS_SEARCH_PARAM,
            value: nextStatusFilter,
            defaultValue: ALL_ROUTINE_STATUSES_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_ROUTINE_SEARCH_QUERY_SEARCH_PARAM,
            value: nextSearchQuery,
            defaultValue: "",
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_ROUTINE_SORT_SEARCH_PARAM,
            value: nextSortFilter,
            defaultValue: DEFAULT_ROUTINE_SORT,
          });

          return nextSearchParams;
        },
        { replace: true, state: locationState }
      );
    },
    [locationState, setSearchParams]
  );

  return {
    statusFilter,
    searchQuery,
    sortFilter,
    updateRoutineFilters,
  };
};
